import { useState } from "react";
import { ChevronDown, BadgeCheck, ShieldCheck, Clock3 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { FaWhatsapp } from "react-icons/fa";
import faqIllustration from "../assets/illustrations/faq_illustration.png";

const COLORS = {
  primary: "#3AAFA9",        // Soft Teal (Main Brand Color)
  secondary: "#7BC8A4",      // Warm Green
  background: "#F7FAF9",     // Off-White Background
  accent: "#F4A896",         // Peach Coral Accent
  textMain: "#2E2E2E",       // Charcoal Grey
  textSub: "#4B5563",        // Sub Text
  textMuted: "#9CA3AF",      // Muted Text
  line: "#E6E8EC",           // Border / Divider
  error: "#EF4444",          // Error State
};

const faqs = [
  {
    question: "What if my parents are uncomfortable with the assigned care partner?",
    answer:
      "Your parents' comfort is our top priority. If they're not comfortable for any reason, simply inform your account manager. We'll assign a new care partner within 48 hours at no additional cost. We want your parents to feel like they're interacting with a trusted family friend.",
  },
  {
    question: "How do you ensure care partners don't steal or harm my parents?",
    answer:
      "Every care partner undergoes police verification and background checks. All visits are GPS tracked with mandatory photo documentation. We maintain a security deposit from every care partner. Additionally, we have insurance coverage for any incidents. In 2+ years of operation, we've had zero security incidents.",
  },
  {
    question: "What happens to my data if I cancel the service?",
    answer:
      "Upon cancellation, your data is retained for 90 days in case you decide to restart the service. After 90 days, all personal data is permanently deleted from our systems. You can also request immediate deletion at any time by contacting our data protection officer.",
  },
  {
    question: "Can care partners access my parents' financial information?",
    answer:
      "Absolutely not. Care partners are strictly prohibited from handling any financial matters, ATM cards, or bank documents. All financial coordination (if needed) goes through your account manager with your explicit approval for each transaction. Any violation results in immediate termination and legal action.",
  },
  {
    question: "How do I verify that visits are actually happening?",
    answer:
      "Every visit is documented with timestamped photos, GPS location, and visit notes. You receive real-time notifications when the care partner arrives and leaves. You can also call your parents immediately after any visit to verify. All visit history is permanently logged in your family dashboard.",
  },
];

const trustStats = [
  { icon: ShieldCheck, label: "Verified Process" },
  { icon: BadgeCheck, label: "Quality-Assured Care" },
  { icon: Clock3, label: "Fast Response Support" },
];

const FAQ = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section
      id="faq"
      className="relative overflow-hidden px-4 sm:px-6 pt-16 sm:pt-24 pb-20 sm:pb-28"
      style={{
        background:
          `radial-gradient(circle at 14% 18%, rgba(58,175,169,0.08), transparent 34%), radial-gradient(circle at 88% 82%, rgba(123,200,164,0.12), transparent 36%), ${COLORS.background}`,
      }}
    >
      {/* Decorative Blur Spot */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#3AAFA9]/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="mx-auto max-w-6xl relative z-10">
        
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          viewport={{ once: true }}
          className="mb-12 text-center"
        >
          <div
            className="inline-flex items-center rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] backdrop-blur-md"
            style={{
              borderColor: `${COLORS.primary}40`,
              backgroundColor: `${COLORS.primary}12`,
              color: COLORS.primary,
            }}
          >
            Trust & Transparency
          </div>
          <h2
            className="mt-5 text-3xl sm:text-4xl md:text-5xl font-black leading-tight tracking-tight"
            style={{ color: COLORS.textMain }}
          >
            Frequently Asked Questions
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm sm:text-base leading-relaxed font-medium" style={{ color: COLORS.textSub }}>
            Clear answers about care quality, verification, and support so your
            family can decide with confidence.
          </p>
        </motion.div>

        {/* Trust Stats Capsules */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
          viewport={{ once: true }}
          className="mb-14 grid gap-4 grid-cols-1 sm:grid-cols-3"
        >
          {trustStats.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="group rounded-2xl border bg-white/60 hover:bg-white px-5 py-4 text-sm font-bold shadow-[0_12px_30px_rgba(58,175,169,0.03)] backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_36px_rgba(58,175,169,0.08)]"
              style={{ borderColor: `${COLORS.primary}20`, color: COLORS.textMain }}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#3AAFA9]/10 flex items-center justify-center text-[#3AAFA9] group-hover:bg-[#3AAFA9] group-hover:text-white transition-colors duration-300">
                  <Icon size={16} />
                </div>
                <span>{label}</span>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Content Layout Split */}
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-2">
          
          {/* Accordion Questions List */}
          <div className="space-y-4">
            {faqs.map((item, index) => {
              const isActive = index === activeIndex;

              return (
                <motion.div
                  key={index}
                  onClick={() => setActiveIndex(index)}
                  className={`cursor-pointer rounded-2xl border p-5 transition-all duration-500 overflow-hidden ${
                    isActive
                      ? "bg-white shadow-[0_20px_48px_rgba(58,175,169,0.12)]"
                      : "bg-white/60 hover:bg-white border-[#E6E8EC]/80 hover:border-[#3AAFA9]/30"
                  }`}
                  style={{
                    borderColor: isActive ? COLORS.primary : "#E6E8EC",
                  }}
                >
                  <div className="flex items-center justify-between gap-4">
                    <h3 className="text-base sm:text-lg font-bold tracking-tight" style={{ color: COLORS.textMain }}>
                      {item.question}
                    </h3>
                    <div
                      className="rounded-full p-2.5 transition-colors duration-300 shrink-0"
                      style={{
                        backgroundColor: isActive
                          ? `${COLORS.primary}25`
                          : `${COLORS.primary}08`,
                      }}
                    >
                      <motion.div
                        animate={{ rotate: isActive ? 180 : 0 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                      >
                        <ChevronDown size={16} style={{ color: COLORS.primary }} />
                      </motion.div>
                    </div>
                  </div>

                  <AnimatePresence initial={false}>
                    {isActive && (
                      <motion.div
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        variants={{
                          open: { opacity: 1, height: "auto", marginTop: 14 },
                          collapsed: { opacity: 0, height: 0, marginTop: 0 }
                        }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="text-sm sm:text-base leading-relaxed" style={{ color: COLORS.textSub }}>
                          {item.answer}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}

            {/* Still Have Questions CTA */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-8 p-6 rounded-[2rem] border bg-white/40 backdrop-blur-sm flex flex-col sm:flex-row items-center justify-between gap-6 shadow-[0_12px_30px_rgba(58,175,169,0.02)]"
              style={{ borderColor: `${COLORS.primary}15` }}
            >
              <div>
                <h4 className="text-sm sm:text-base font-extrabold text-[#2E2E2E]">Still have questions?</h4>
                <p className="text-xs text-[#4B5563] mt-1 font-medium">Chat with our dedicated care consultants on WhatsApp.</p>
              </div>
              <a
                href="https://wa.me/919098114568?text=Hello%20JANAK%2C%20I%20have%20some%20questions%20about%20the%20care%20plans."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 rounded-xl bg-[#25D366] px-5 py-3 text-xs font-bold text-white shadow-md hover:shadow-lg transition-transform hover:-translate-y-0.5"
              >
                <FaWhatsapp size={14} />
                Ask a Question
              </a>
            </motion.div>
          </div>

          {/* Right Illustration Dashboard Panel */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative h-[300px] sm:h-[380px] lg:h-[480px] w-full overflow-hidden rounded-[2.5rem] border shadow-[0_24px_55px_rgba(58,175,169,0.06)] bg-white flex items-center justify-center p-6 sm:p-10 hover:shadow-[0_32px_64px_rgba(58,175,169,0.1)] transition-shadow duration-500"
            style={{ borderColor: `${COLORS.primary}15` }}
          >
            <img
              src={faqIllustration}
              alt="Support Center FAQs"
              className="w-full h-full object-contain"
            />
          </motion.div>
          
        </div>
      </div>
    </section>
  );
};

export default FAQ;

